// IslandSlideOver — the shared right-anchored "island" slide-over chrome.
//
// One component for every drawer-style modal so they look identical: portal to
// <body>, dimmed backdrop, and a floating panel inset on all sides (rounded-2xl
// + border + shadow) that matches the island content card. Click-outside +
// Esc close. Callers render their own header/body/footer as children.

import React, { useEffect } from 'react';
import { ModalPortal } from './ModalPortal';

interface IslandSlideOverProps {
    onClose: () => void;
    /** Max width of the panel (Tailwind class). Default max-w-3xl. */
    widthClass?: string;
    /** Stacking layer (Tailwind z class). Default z-50. Raise it when the
     *  slide-over can open above another overlay. */
    zClass?: string;
    /** Fill the viewport (minus the island margin) instead of anchoring right
     *  as a width-constrained drawer. Use for large surfaces (viewers, chat). */
    fullScreen?: boolean;
    children: React.ReactNode;
}

export const IslandSlideOver: React.FC<IslandSlideOverProps> = ({ onClose, widthClass = 'max-w-3xl', zClass = 'z-50', fullScreen = false, children }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <ModalPortal>
            <div
                className={`fixed inset-0 ${zClass} bg-black/40 backdrop-blur-sm flex p-3 sm:p-4 ${fullScreen ? '' : 'justify-end'}`}
                onClick={onClose}
            >
                <div
                    className={`h-full w-full ${fullScreen ? '' : widthClass} bg-white rounded-2xl border-2 border-gray-100 shadow-2xl flex flex-col overflow-hidden`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {children}
                </div>
            </div>
        </ModalPortal>
    );
};

export default IslandSlideOver;
